import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Upload, X, ChevronUp, ChevronDown, Save } from "lucide-react";

const SECTIONS = [
  { slug: "home-banner",    label: "Home Banner" },
  { slug: "art-centre",     label: "Art Centre" },
  { slug: "ceramic-studio", label: "Ceramic Studio" },
];

export default function AdminPageGalleries() {
  const [section, setSection] = useState("art-centre");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/page-galleries/${s}`);
      setPhotos(data.photos || []);
      setDirty(false);
    } catch (e) { toast.error("Failed to load gallery"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(section); }, [section]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", `page-gallery-${section}`);
    setUploading(true);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(p => [...p, { url: data.url, caption: "" }]);
      setDirty(true);
      toast.success("Uploaded — remember to Save");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploading(false); }
  };

  const setCaption = (i, v) => { setPhotos(p => p.map((x, j) => j === i ? { ...x, caption: v } : x)); setDirty(true); };
  const setLinkUrl = (i, v) => { setPhotos(p => p.map((x, j) => j === i ? { ...x, link_url: v } : x)); setDirty(true); };
  const remove = (i) => { setPhotos(p => p.filter((_, j) => j !== i)); setDirty(true); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    setPhotos(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/page-galleries/${section}`, { photos });
      toast.success("Gallery saved");
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-page-galleries">
      <Toaster position="top-center" richColors />

      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="overline">On-page galleries</div>
          <h1 className="display text-4xl mt-2">Site imagery</h1>
          <p className="text-muted2 text-sm mt-2">The <strong>Home Banner</strong> shows a full-width strip on the home page (use for exhibition promos). Art Centre and Ceramic Studio photos power the lightbox on those pages.</p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`btn-primary ${!dirty || saving ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid="pg-save"
        >
          <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 mb-8">
        {SECTIONS.map(s => (
          <button
            key={s.slug}
            onClick={() => {
              if (dirty && !window.confirm("Unsaved changes will be lost. Switch section?")) return;
              setSection(s.slug);
            }}
            className={`text-xs px-4 py-2 border ${section === s.slug ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"}`}
            style={section === s.slug ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
            data-testid={`pg-tab-${s.slug}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted2">Loading…</div>
      ) : (
        <div className="space-y-3">
          {photos.map((p, i) => (
            <div key={i} className="border border-soft bg-white p-3 flex gap-4 items-center" data-testid={`pg-row-${i}`}>
              <div className="w-24 h-24 bg-cream-2 border border-soft overflow-hidden shrink-0 flex items-center justify-center p-1">
                <img src={p.url} alt="" className="max-w-full max-h-full w-auto h-auto object-contain" />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={p.caption}
                  onChange={(e) => setCaption(i, e.target.value)}
                  placeholder="Caption (e.g. 'Watercolour session · Sunday Art with David')"
                  className="w-full bg-transparent border-b border-soft py-1.5 text-sm focus:outline-none focus:border-charcoal"
                  data-testid={`pg-caption-${i}`}
                />
                {section === "home-banner" && (
                  <input
                    type="text"
                    value={p.link_url || ""}
                    onChange={(e) => setLinkUrl(i, e.target.value)}
                    placeholder="Link (optional) — e.g. /events/solo-show-bismarck-dias  or  https://…"
                    className="w-full bg-transparent border-b border-soft py-1.5 mt-2 text-xs focus:outline-none focus:border-charcoal"
                    data-testid={`pg-link-${i}`}
                  />
                )}
                <div className="text-[10px] text-muted2 mt-1 truncate">{p.url}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 border border-soft hover:bg-cream-2 disabled:opacity-30" data-testid={`pg-up-${i}`}><ChevronUp size={12} /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === photos.length - 1} className="p-1 border border-soft hover:bg-cream-2 disabled:opacity-30" data-testid={`pg-down-${i}`}><ChevronDown size={12} /></button>
              </div>
              <button type="button" onClick={() => remove(i)} className="p-2 hover:bg-cream-2 text-red-700" data-testid={`pg-remove-${i}`}><X size={14} /></button>
            </div>
          ))}

          <label className="inline-flex items-center gap-2 border border-dashed border-soft px-4 py-3 cursor-pointer hover:bg-cream-2 text-sm mt-4" data-testid="pg-upload-label">
            <Upload size={14} /> {uploading ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="pg-upload-input" />
          </label>

          {photos.length === 0 && !uploading && <div className="text-muted2 text-sm mt-2">No photos yet — add your first.</div>}
        </div>
      )}
    </div>
  );
}
